import {
  MenuMappingCustomizationCustomizationOption,
  MenuMappingCustomizationOptionResource,
  MenuMappingSubsectionItemResource,
  TypeName,
} from 'api'
import { getMenuMappingSubsection } from 'api/menuMapping/subsections'
import { 
  updateMenuMappingCustomizationCustomizationOption,
  getMenuMappingCustomizationCustomizationOptions,
} from 'api/menuMapping/customizationOptions'
import { ModifierValuesMap } from '../MenuSectionEditor'
import { NESTED_GROUP_PREFIX } from '../MenuItemViewModal/index'

/**
 * Helper function to find a group ID by matching a modifier title in the nested modifiers map.
 * 
 * @param modifierTitle - The title of the modifier to search for
 * @param nestedModsToAdd - Map of nested modifiers to search through
 * @returns The group ID if found, undefined otherwise
 */
const findGroupIdByModifierTitle = (modifierTitle: string, nestedModsToAdd: ModifierValuesMap): string | undefined => {
  return Object.entries(nestedModsToAdd).find(([_, values]) => 
    values.title === modifierTitle
  )?.[1].parentGroupId
}

/**
 * Helper function to calculate the next available sort position for an item in a subsection
 * and find any existing subsection item for a given item ID.
 * 
 * For new items, we use max position + 1 to ensure we don't create duplicate positions.
 * This works because:
 * 1. When we create a new item, the backend automatically creates a subsection item for it with null position
 * 2. We want to use max + 1 to ensure the new item gets a unique position
 * 3. This prevents having multiple items with the same position
 * 
 * For existing items when ALL items have positions, uses the max position + 1 to ensure we don't overwrite existing positions.
 * This works because:
 * 1. When updating an item and all items have positions, we want to preserve the order
 * 2. Using max position + 1 ensures we don't conflict with existing positions
 * 
 * @param subsectionId - The ID of the subsection to check
 * @param targetItemId - Optional ID of the item to find in the subsection
 * @returns Object containing:
 *   - nextSortPosition: The next available sort position (1 if no items exist)
 *   - existingSubsectionItem: The existing subsection item if found, undefined otherwise
 */
export const getNextItemSortPosition = async (subsectionId: string, targetItemId?: string): Promise<{ 
  nextSortPosition: number
  existingSubsectionItem?: MenuMappingSubsectionItemResource 
}> => {
  try {
    // Get subsection data including subsection items
    const subsectionResponse = await getMenuMappingSubsection(subsectionId, { include: 'subsection_items' })
    
    // Get subsection items from included data
    const subsectionItems = (subsectionResponse.included || []).filter(
      (item): item is MenuMappingSubsectionItemResource => 
        item.type === TypeName.MenuMappingSubsectionItem &&
        item.relationships.subsection.data.id === subsectionId
    )

    // If no items exist, return 1
    if (subsectionItems.length === 0) {
      return { nextSortPosition: 1 }
    }

    // Find existing subsection item if targetItemId is provided
    const existingSubsectionItem = targetItemId ? subsectionItems.find(
      item => item.relationships.item.data.id === targetItemId
    ) : undefined

    // Get all existing positions (excluding null positions)
    const existingPositions = subsectionItems
      .map(item => item.attributes.item_sort_position)
      .filter((pos): pos is number => pos != null)

    // If we have any positions, use maxPosition + 1
    // This ensures we don't create duplicate positions
    if (existingPositions.length > 0) {
      return {
        nextSortPosition: Math.max(...existingPositions) + 1,
        existingSubsectionItem
      }
    }

    // If all items have null positions, use total count
    return { 
      nextSortPosition: subsectionItems.length,
      existingSubsectionItem 
    }
  } catch (err) {
    console.error('Error getting next sort position:', err)
    return { nextSortPosition: 1 }
  }
}

/**
 * Helper function to calculate and update sort positions for a group of modifiers.
 * This ensures modifiers are ordered correctly within their group.
 * 
 * @param params - Object containing:
 *   - modifiers: Array of modifier resources to update
 *   - existingOptions: Array of existing customization options in the group
 * 
 * The function:
 * 1. Calculates the next available sort position based on existing options
 * 2. Pre-calculates sort positions for each modifier
 * 3. Updates all modifiers in parallel with their new sort positions
 * 4. Preserves existing prices while updating sort positions
 */
const calculateAndUpdateModifierSortPositions = async ({
  modifiers,
  existingOptions,
}: {
  modifiers: MenuMappingCustomizationOptionResource[]
  existingOptions: MenuMappingCustomizationCustomizationOption[]
}) => {
  // Calculate max sort position and next position
  const maxSortPosition = Math.max(...existingOptions.map(option => 
    option.attributes.customization_option_sort_position || 0
  ))
  let nextSortPosition = maxSortPosition + 1

  // Pre-calculate sort positions for each modifier
  const modifierSortPositions = modifiers.reduce((acc, mod) => {
    // @ts-ignore - mod.data.id is correct.
    acc[mod.data.id] = nextSortPosition++
    return acc
  }, {} as Record<string, number>)

  // Create a map of modifier IDs to their customization_customization_option IDs
  const modifierCustomizationOptionMap = modifiers.reduce((acc, mod) => {
    const customizationOption = existingOptions.find(
      // @ts-ignore - mod.data.id is correct.
      option => option.relationships.customization_option.data.id === mod.data.id
    )
    if (customizationOption) {
      // @ts-ignore - mod.data.id is correct.
      acc[mod.data.id] = customizationOption.id
    }
    return acc
  }, {} as Record<string, string>)

  // Update all modifiers in parallel with pre-calculated sort positions
  await Promise.all(
    modifiers.map(async (mod) => {
      const customizationOption = existingOptions.find(
        // @ts-ignore - mod.data.id is correct.
        option => option.relationships.customization_option.data.id === mod.data.id
      )

      // @ts-ignore - mod.data.id is correct.
      if (customizationOption && modifierCustomizationOptionMap[mod.data.id]) {
        // Update only the sort position using pre-calculated value
        await updateMenuMappingCustomizationCustomizationOption({
          attributes: {
            // @ts-ignore - mod.data.id is correct.
            customization_option_sort_position: modifierSortPositions[mod.data.id],
            customization_option_price: customizationOption.attributes.customization_option_price, // Keep existing price
          },
          // @ts-ignore - mod.data.id is correct.
          customizationCustomizationOptionId: modifierCustomizationOptionMap[mod.data.id],
        })
      }
    })
  )
}

/**
 * Helper function to update sort positions for newly created modifiers.
 * This function handles both regular and nested modifiers, grouping them by their parent group
 * and updating their sort positions in parallel.
 * 
 * @param params - Object containing:
 *   - modifiers: Array of newly created modifier resources
 *   - modifiersToAdd: Map of modifier values used to find parent groups
 *   - groupIdsMap: Map of temporary IDs to actual group IDs (can be either regular or nested group IDs)
 * 
 * The function:
 * 1. Groups modifiers by their parent group
 * 2. For each group:
 *    - Gets the correct customization ID
 *    - Fetches existing options
 *    - Updates sort positions for all modifiers in the group
 */
export const updateNewModifiersSortPositions = async ({
  modifiers,
  modifiersToAdd,
  groupIdsMap,
}: {
  modifiers: MenuMappingCustomizationOptionResource[]
  modifiersToAdd: ModifierValuesMap
  groupIdsMap: { [tempId: string]: string }
}) => {
  if (!modifiers.length) return

  // Group modifiers by their parent group
  const modifiersByGroup = modifiers.reduce((acc, mod) => {
    // @ts-ignore - mod.data.attributes.title is correct.
    const groupId = findGroupIdByModifierTitle(mod.data.attributes.title, modifiersToAdd)

    if (groupId) {
      if (!acc[groupId]) {
        acc[groupId] = []
      }
      acc[groupId].push(mod)
    }
    return acc
  }, {} as Record<string, MenuMappingCustomizationOptionResource[]>)

  // Update sort positions for each group's modifiers
  await Promise.all(
    Object.entries(modifiersByGroup).map(async ([groupId, groupModifiers]) => {
      // For nested modifiers, the groupId is already the full nested group ID
      // For regular modifiers, we need to check if it's a temp ID
      const customizationId = groupId.includes(NESTED_GROUP_PREFIX)
        ? groupIdsMap[groupId] // Use the full nested group ID to look up the customization ID
        : groupId.startsWith('temp-')
          ? groupIdsMap[groupId]
          : groupId

      if (customizationId) {
        const existingOptions = await getMenuMappingCustomizationCustomizationOptions({ customizationId })
        await calculateAndUpdateModifierSortPositions({
          modifiers: groupModifiers,
          existingOptions,
        })
      }
    })
  )
}